<template>
  <div class="my-6">
    <Combobox v-model="selected" nullable v-slot="{ open }">
      <div class="relative mt-1">
        <div
          class="relative rounded-lg bg-stone-100 p-2 max-w-lg opacity-90 cursor-default overflow-hidden text-left shadow-stone-300 shadow-md focus:outline-none focus-visible:(ring-2 ring-stone-400 ring-opacity-75 ring-offset-2 ring-offset-stone-300) sm:text-sm"
          :class="{
            'focus-within:(bg-white shadow-lg ring-1 ring-stone-400 ring-opacity-75 ring-offset-1 ring-offset-stone-300)':
              selected == null,
          }"
        >
          <ComboboxInput
            class="w-full border-none pl-2 py-2 pr-10 text-lg leading-5 text-stone-600 focus:(outline-none ring-0) focus-visible:placeholder-stone-400 bg-transparent placeholder-stone-500 relative"
            :displayValue="(result) => result?.name"
            placeholder="Search for a location"
            open="true"
            @change="query = $event.target.value"
            v-show="selected == null"
          />
          <ComboboxButton
            class="absolute inset-0"
            v-if="selected == null && query.length === 0 && !open"
            aria-label="Open location suggestions and search"
          ></ComboboxButton>
          <div v-if="selected" class="w-full py-1 px-2 text-lg leading-5 text-stone-600 flex gap-2 items-center">
            <div i-ic-baseline-my-location animate-pulse v-if="props.isWatching" aria-hidden="true"></div>
            <div class="flex-grow truncate" :title="selected?.name">{{ selected?.name }}</div>
            <ComboboxButton
              class="text-white font-mono text-4 !bg-stone-400 px-2 py-1 rounded shadow-stone-200 shadow-md hover:(opacity-90 shadow-stone-300 !bg-stone-500 shadow-md) active:(!bg-stone-600 shadow-stone-400)"
              @click="selected = null"
            >
              Change
            </ComboboxButton>
          </div>
        </div>
        <TransitionRoot
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          @after-leave="query = ''"
        >
          <ComboboxOptions
            class="absolute mt-2 max-h-60 w-full max-w-md overflow-auto rounded-md bg-white text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-10"
          >
            <div
              v-if="results.length === 0 && query.length > 3 && !isLoading"
              class="relative cursor-default select-none py-3 px-4 text-stone-400"
            >
              Nothing found.
            </div>
            <!-- <div
              v-if="isLoading && query.length > 3"
              class="flex gap-1 items-end justify-center cursor-default select-none py-3 px-4 text-stone-400"
            >
              Loading
              <div class="i-svg-spinners-3-dots-fade inline-block" aria-hidden="true" />
            </div> -->

            <ComboboxOption
              v-for="result in results"
              as="template"
              :key="result.name"
              :value="result"
              v-slot="{ selected, active }"
            >
              <li
                class="relative cursor-default select-none py-3 px-4 text-md grid grid-cols-[auto_1fr] gap-x-2 items-center"
                :class="{
                  'bg-orange-400 text-white': active,
                  'text-stone-600': !active,
                }"
              >
                <div
                  class="inline-block opacity-90"
                  :class="{
                    'i-ic-baseline-my-location': result.module === 'currentLocation',
                    'i-material-symbols-search-rounded': ['bingSearch', 'mapkitSearch'].includes(result.module),
                    'i-material-symbols-pin-drop-rounded': result.module === 'latLng',
                    'i-material-symbols-history-rounded': result.module === 'history',
                  }"
                  aria-hidden="true"
                ></div>

                <div truncate :class="{ 'font-medium': selected, 'font-normal': !selected }">
                  {{ result.name }}
                </div>
                <div v-if="result?.subtitle"></div>
                <div opacity-70 truncate v-if="result?.subtitle">{{ result.subtitle }}</div>
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </TransitionRoot>
      </div>
    </Combobox>
  </div>
</template>
<script setup>
import { useVModel } from "@vueuse/core";
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  TransitionRoot,
} from "@headlessui/vue";
const props = defineProps({
  modelValue: {
    type: Object,
    default: null,
  },
  isWatching: {
    type: Boolean,
    default: false,
  },
  getLocationOnLoad: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["update:modelValue"]);
const selected = useVModel(props, "modelValue", emit);

const query = ref("");
const { data: cloudflareData } = await useFetch("/api/cf");
const { locationPermissionStatus, location, updateLocation } = useLocation();
if (locationPermissionStatus.value === "allowed") {
  updateLocation();
}
const config = useRuntimeConfig();

const searchLocation = computed(() => {
  return {
    latitude: location.value?.latitude ?? parseFloat(cloudflareData.value?.latitude),
    longitude: location.value?.longitude ?? parseFloat(cloudflareData.value?.longitude),
  };
});
const { results, isLoading, error } = useSearch(query, selected, searchLocation);

useHead({
  script: [
    {
      src: "https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.core.js",
      async: true,
      crossorigin: "anonymous",
      "data-libraries": "services",
      "data-token": config.public.mapkitApiKey,
    },
  ],
});
</script>
